import { FC, ChangeEvent } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputProps } from 'antd';
import cn from 'clsx';
import { Controller, useFormContext } from 'react-hook-form';

import styles from '../form.module.scss';

export interface IFormInputProps extends InputProps {
  name: string;
  type?: 'text' | 'password';
  label?: string;
  labelPosition?: 'top' | 'left';
  required?: boolean;
  uppercase?: boolean;
  className?: string;
  onBlur?: () => void;
  onSearch?: () => void;
}

export const FormSearch: FC<IFormInputProps> = ({
  name,
  type = 'text',
  label,
  labelPosition = 'top',
  required,
  className,
  uppercase = false,
  onBlur,
  onSearch,
  ...props
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value, name, onBlur: internalFormBlur },
        fieldState: { error, invalid },
      }) => {
        const handleOnChange = (evt: ChangeEvent<HTMLInputElement>) => {
          const val = evt.target.value;
          onChange(uppercase ? (val ?? '').toUpperCase() : val);
        };

        const handleSearch = () => {
          onSearch?.();
        };

        const handleBlur = () => {
          if (invalid) {
            return;
          }

          onBlur?.();
          internalFormBlur();
        };

        return (
          <div
            className={cn(
              styles.container,
              styles[`label-${labelPosition}`],
              className,
            )}
          >
            {!!label && (
              <label htmlFor={name} className={styles.label}>
                {label}
                {required && <span className={styles.required}>*</span>}
              </label>
            )}
            <Input.Search
              id={name}
              onChange={handleOnChange}
              value={value}
              name={name}
              status={!!error ? 'error' : ''}
              enterButton={
                <Button icon={<SearchOutlined />} disabled={!value} />
              }
              onBlur={handleBlur}
              onSearch={handleSearch}
              {...props}
            />
            {!!error && <div className={styles.error}>{error?.message}</div>}
          </div>
        );
      }}
    />
  );
};
